import clsx from 'clsx';
import { Activity, useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';

import {
  IconEye,
  IconTrash,
  IconXboxX,
  IconSearch,
  IconMoodKid,
  IconLockOpen,
  IconMoodCheck,
  IconClockHour3,
  IconAdjustments,
} from '@tabler/icons-react';

import ActionDropdown from '@/components/ActionDropdown';
import { AvatarGroupList } from '@/components/Avatar';
import Button from '@/components/Button/Button';
import ConfirmationModal from '@/components/ConfirmationModal';
import Input from '@/components/Input/Input';
import ProgressBar from '@/components/ProgressBar';
import { SummaryCard } from '@/components/SummaryCard';
import CustomTable from '@/components/Table/index';
import { LessonStageConfig, CommonStatusConfig } from '@/constants/constant';
import {
  ECommonStatus,
  ESubscriberType,
  ELessonStageType,
  ERestoreEntityType,
} from '@/enum';
import { useDebounce } from '@/hooks/useDebounce';
import { useFormatText } from '@/hooks/useFormatText';
import usePageTitle from '@/hooks/usePageTitle';
import {
  softdeleteRecord,
  blockUnblockRecordAPI,
} from '@/service/commonService';
import {
  getSchoolStudentList,
  getSchoolStudentSummary,
} from '@/service/schoolService';
import {
  IStudentData,
  IStatsDataItem,
  ISelectedRowValue,
  IStudentFilterValues,
} from '@/types';
import {
  pickBy,
  getAssetUrl,
  customPredicate,
  formatLastActive,
  getStartOfMonthUnix,
  formatSecondsToDuration,
} from '@/utils';

import ChildParentDetailsModal from '../children/modal/ChildParentDetailsModal';
import StudentFilterModal from './modals/StudentFilterModal';

const STUDENT_SUMMARY_CONFIG = [
  {
    key: 'totalStudents',
    icon: <IconMoodKid color="#717784" />,
    label: 'user-management.student.total-children',
  },
  {
    key: 'activeStudent',
    icon: <IconMoodCheck color="#717784" />,
    label: 'user-management.student.active-children',
  },
  {
    key: 'avgScreenTime',
    icon: <IconClockHour3 color="#717784" />,
    label: 'user-management.student.average-screen-time',
  },
];
import useCommonStore from '@/store/useCommonStore';

const StudentList = () => {
  const { id } = useParams<{ id: string }>();
  const { setTitle, setIsBreadcrumb } = usePageTitle();
  const { formatText } = useFormatText();
  const { loadingTable, setLoadingTable, loadingSummary, setLoadingSummary } =
    useCommonStore();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search?.trim(), 500);
  const location = useLocation();

  const [students, setStudents] = useState<IStudentData[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [sortField, setSortField] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>('ASC');
  const [statsData, setStatsData] = useState<IStatsDataItem[]>(
    STUDENT_SUMMARY_CONFIG.map((item) => ({
      ...item,
      value: '0',
      trend: '0%',
      trendLabel: 'common.this-month',
      trendDirection: 'STABLE',
    }))
  );

  // Modal states
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isRemoveStudentModalOpen, setIsRemoveStudentModalOpen] =
    useState(false);
  const [isBlockModalOpen, setIsBlockModalOpen] = useState(false);
  const [isStudentDetailsModalOpen, setIsStudentDetailsModalOpen] =
    useState(false);
  const [selectedStudent, setSelectedStudent] =
    useState<ISelectedRowValue | null>(null);
  const [confirmationLoading, setConfirmationLoading] = useState(false);

  // Filter states
  const [filterValues, setFilterValues] = useState<IStudentFilterValues>({
    status: undefined,
    levels: undefined,
    assignedClass: undefined,
  });

  const schoolName = location?.state?.school_name;

  const handleFilterApply = (filters: IStudentFilterValues) => {
    setFilterValues(filters);
  };

  useEffect(() => {
    setTitle(formatText('sidebar.schools'));
    setIsBreadcrumb([
      { label: formatText('sidebar.user-management') },
      {
        label: schoolName ? String(schoolName) : formatText('sidebar.school'),
        to: '/user-management/school',
      },
      { label: formatText('user-management.students') },
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolName]);

  const fetchStudents = async () => {
    if (!id) return;
    setLoadingTable(true);
    const params = pickBy(
      {
        search: debouncedSearch || undefined,
        school_id: id,
        offset: (currentPage - 1) * pageSize,
        limit: pageSize,
        order: sortField ? { [sortField]: sortDirection } : undefined,
        status: filterValues?.status || undefined,
        stages:
          Array.isArray(filterValues?.levels) && filterValues?.levels.length > 0
            ? filterValues?.levels
            : undefined,
        class_id:
          Array.isArray(filterValues?.assignedClass) &&
          filterValues?.assignedClass.length > 0
            ? filterValues?.assignedClass
            : undefined,
      },
      customPredicate
    );
    const response = await getSchoolStudentList(id, params);
    if (response?.status) {
      setStudents(response?.data || []);
      setTotal(response?.total || 0);
    }
    setLoadingTable(false);
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch]);

  useEffect(() => {
    fetchStudents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    id,
    debouncedSearch,
    currentPage,
    pageSize,
    sortField,
    sortDirection,
    filterValues,
  ]);

  const fetchSummary = async () => {
    if (!id) return;
    setLoadingSummary(true);
    const monthTimestamp = getStartOfMonthUnix();

    const response = await getSchoolStudentSummary({
      month: monthTimestamp,
      school_id: id,
    });

    if (response?.status) {
      const apiData = response?.data;
      const mappedSummary = STUDENT_SUMMARY_CONFIG.map((item) => {
        const data = apiData[item.key];
        const value =
          item.key === 'avgScreenTime'
            ? formatSecondsToDuration(data?.total)
            : ((data?.total || 0)?.toLocaleString?.() ?? data?.total ?? '0');
        return {
          ...item,
          value,
          trend: `${data?.percentage || 0}%`,
          trendLabel: 'common.this-month',
          trendDirection: (data?.trend || 'STABLE')?.toUpperCase(),
          isPositive: data?.is_positive,
        };
      });
      setStatsData(mappedSummary);
    }
    setLoadingSummary(false);
  };

  useEffect(() => {
    fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleSort = (key: string) => {
    if (sortField === key) {
      setSortDirection(sortDirection === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortField(key);
      setSortDirection('ASC');
    }
  };

  const handlefetch = () => {
    fetchStudents();
    fetchSummary();
  };

  const handleBlockStudent = async (student = selectedStudent) => {
    if (!student || !id) return;

    setConfirmationLoading(true);

    try {
      const params = {
        id: student.id,
        record_type: ERestoreEntityType.STUDENT,
        is_block: student.status === ECommonStatus.ACTIVE,
      };
      const response = await blockUnblockRecordAPI(params);

      if (response?.status) {
        handlefetch();
      } else {
        console.error(response?.message);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setConfirmationLoading(false);
      setIsBlockModalOpen(false);
      setSelectedStudent(null);
    }
  };

  const handleRemoveStudent = async () => {
    if (!selectedStudent?.id) return;
    setConfirmationLoading(true);

    try {
      const params = {
        id: selectedStudent.id.toString(),
        record_type: ERestoreEntityType.STUDENT,
      };
      const response = await softdeleteRecord(params);
      if (response?.status) {
        handlefetch();
      }
    } catch (error) {
      console.error(error);
    } finally {
      setConfirmationLoading(false);
      setIsRemoveStudentModalOpen(false);
      setSelectedStudent(null);
    }
  };

  const columns = [
    {
      title: 'user-management.student.column.name',
      key: 'name',
      width: '240px',
      sorter: true,
      handleSort: () => handleSort('name'),
      render: (row: IStudentData) => (
        <div className="flex items-center gap-2">
          <AvatarGroupList
            list={[
              {
                id: row.id,
                name: row.name,
                image: getAssetUrl(row?.asset) || row?.user_img,
              },
            ]}
            size={40}
            avatarClassName="w-10 h-10 text-sm"
          />
          <span className="font-medium text-neutral-primary capitalize">
            {row?.name}
          </span>
        </div>
      ),
    },
    {
      title: 'user-management.student.column.class',
      key: 'class_name',
      width: '160px',
      sorter: true,
      handleSort: () => handleSort('class_name'),
      render: (row: { class_name?: string }) => (
        <span>{row?.class_name ?? '-'}</span>
      ),
    },
    {
      title: 'user-management.student.column.level',
      key: 'stage',
      width: '180px',
      sorter: true,
      handleSort: () => handleSort('stage'),
      render: (row: { stage: ELessonStageType }) => {
        const levelConfig = LessonStageConfig[row.stage] ?? undefined;
        return (
          <span
            className={`px-2 py-1 w-fit rounded-full text-xs font-semibold whitespace-nowrap ${
              levelConfig?.className ?? ''
            }`}
          >
            {levelConfig?.label ? formatText(levelConfig.label) : row?.stage}
          </span>
        );
      },
    },
    {
      title: 'user-management.student.column.progress',
      key: 'progress',
      width: '220px',
      sorter: true,
      handleSort: () => handleSort('progress'),
      render: (row: { progress: number }) => (
        <ProgressBar value={row.progress ?? 0} showPercentage />
      ),
    },
    {
      title: 'user-management.student.column.status',
      key: 'status',
      width: '160px',
      sorter: true,
      handleSort: () => handleSort('status'),
      render: (row: { status: string }) => {
        const config =
          CommonStatusConfig[row?.status as keyof typeof CommonStatusConfig];
        return (
          <span
            className={clsx(
              'px-2 py-1 rounded-full text-xs font-semibold',
              config?.className
            )}
          >
            {formatText(config?.label)}
          </span>
        );
      },
    },
    {
      title: 'user-management.student.column.last-active',
      key: 'last_active',
      width: '180px',
      sorter: true,
      handleSort: () => handleSort('last_active'),
      render: (row: { last_active: number }) =>
        formatLastActive(row?.last_active ?? undefined, formatText),
    },
    {
      title: 'common.actions.action',
      key: 'action',
      width: '100px',
      align: 'center',
      render: (row: IStudentData) => {
        const isBlocked = row?.status === ECommonStatus.IN_ACTIVE;

        const actions = [
          {
            key: 'view',
            label: 'common.actions.view',
            icon: <IconEye size={18} />,
            onClick: () => {
              setSelectedStudent({ name: row?.name, id: row?.id });
              setIsStudentDetailsModalOpen(true);
            },
          },
          {
            key: isBlocked ? 'unblock' : 'block',
            label: isBlocked
              ? 'common.actions.unblock'
              : 'common.actions.block',
            icon: isBlocked ? (
              <IconLockOpen size={18} />
            ) : (
              <IconXboxX size={18} />
            ),
            onClick: () => {
              if (isBlocked) {
                handleBlockStudent({
                  id: row?.id,
                  name: row?.name,
                  status: row?.status,
                });
              } else {
                setSelectedStudent({
                  id: row?.id,
                  name: row?.name,
                  status: row?.status,
                });
                setIsBlockModalOpen(true);
              }
            },
          },
          {
            key: 'remove',
            label: 'common.actions.remove',
            icon: <IconTrash size={18} />,
            isDanger: true,
            onClick: () => {
              setSelectedStudent({
                id: row?.id,
                name: row?.name,
                status: row?.status,
              });
              setIsRemoveStudentModalOpen(true);
            },
          },
        ];

        return <ActionDropdown actions={actions} />;
      },
    },
  ];

  const isFilterApplied =
    !!filterValues?.status ||
    !!(
      Array.isArray(filterValues?.levels) && filterValues?.levels?.length > 0
    ) ||
    !!(
      Array.isArray(filterValues?.assignedClass) &&
      filterValues?.assignedClass?.length > 0
    );

  const showListUI =
    loadingTable ||
    students.length > 0 ||
    search.length > 0 ||
    debouncedSearch.length > 0 ||
    isFilterApplied;

  return (
    <>
      {showListUI && (
        <div className="flex flex-wrap justify-start items-center gap-2.5 mb-6 pl-1">
          <Input
            name="search"
            placeholder={formatText('user-management.student.search-by')}
            value={search}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setSearch(e.target.value)
            }
            leftIcon={{
              icon: <IconSearch size={20} className="text-neutral-500" />,
            }}
            className="max-w-77 md:min-w-77 w-full"
            inputClassName="py-[13px]"
          />
          <div className="relative">
            <Button
              icon={<IconAdjustments size={20} />}
              label={formatText('common.actions.filter')}
              variant="outline"
              className={clsx(
                'px-4 h-12 cursor-pointer',
                isFilterApplied && 'border-brand-default! text-brand-default!'
              )}
              onClick={() => setIsFilterModalOpen(true)}
            />
            {isFilterApplied && (
              <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-brand-default border-2 border-white" />
            )}
          </div>
        </div>
      )}

      {showListUI && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
          {' '}
          {statsData.map((stat, index) => (
            <SummaryCard
              key={index}
              variant="dashboard"
              label={formatText(stat.label)}
              value={stat?.value}
              icon={stat?.icon}
              trendDirection={stat?.trendDirection}
              trendLabel={
                stat?.trendLabel ? formatText(stat.trendLabel) : undefined
              }
              trend={stat?.trend}
              isLoading={loadingSummary}
            />
          ))}
        </div>
      )}

      <div className="mt-6">
        <CustomTable
          isLoading={loadingTable}
          dataSource={students}
          columns={columns}
          total={total}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          fieldName={sortField}
          sortDirection={sortDirection}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setCurrentPage(1);
          }}
          isEmpty={!showListUI}
          emptyTitle={formatText('user-management.student.not-found-title')}
          emptyDescription={formatText(
            'user-management.student.not-found-desc'
          )}
          isEmptySearch={
            students?.length === 0 &&
            (search?.length > 0 ||
              debouncedSearch?.length > 0 ||
              isFilterApplied)
          }
          onRowClick={(id) => {
            const student = students?.find((item) => item?.id === id);
            if (student) {
              setSelectedStudent({ name: student?.name, id: student?.id });
              setIsStudentDetailsModalOpen(true);
            }
          }}
        />
      </div>

      {/* Modals */}
      <Activity mode={isFilterModalOpen ? 'visible' : 'hidden'}>
        <StudentFilterModal
          setIsOpen={setIsFilterModalOpen}
          onApply={handleFilterApply}
        />
      </Activity>

      <Activity mode={isRemoveStudentModalOpen ? 'visible' : 'hidden'}>
        <ConfirmationModal
          isOpen={isRemoveStudentModalOpen}
          setIsOpen={setIsRemoveStudentModalOpen}
          title="children.actions.remove-child-title"
          descriptionPrefix="children.actions.remove-child-prefix"
          descriptionSuffix="children.actions.remove-child-suffix"
          entityName={selectedStudent?.name}
          confirmLabel={formatText('children.actions.remove-child')}
          onConfirm={handleRemoveStudent}
          isLoading={confirmationLoading}
          isDestructive
        />
      </Activity>

      <Activity mode={isBlockModalOpen ? 'visible' : 'hidden'}>
        <ConfirmationModal
          isOpen={isBlockModalOpen}
          setIsOpen={setIsBlockModalOpen}
          title="children.actions.block-child-title"
          descriptionPrefix="children.actions.block-child-prefix"
          descriptionSuffix="children.actions.block-child-suffix"
          entityName={selectedStudent?.name}
          confirmLabel={formatText('children.actions.block-child')}
          onConfirm={handleBlockStudent}
          isLoading={confirmationLoading}
          isDestructive
        />
      </Activity>

      <Activity mode={isStudentDetailsModalOpen ? 'visible' : 'hidden'}>
        <ChildParentDetailsModal
          setIsOpen={setIsStudentDetailsModalOpen}
          selectedChildId={selectedStudent?.id}
          subscriberType={ESubscriberType.SCHOOL}
          onEditSave={fetchStudents}
          isOpenBlockModal={isBlockModalOpen}
          setIsOpenBlockModal={setIsBlockModalOpen}
          setSelectedChildren={setSelectedStudent}
          handleBlockChildren={handleBlockStudent}
        />
      </Activity>
    </>
  );
};
export default StudentList;
